import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import salesRadarService from '../../services/salesRadarService';

// Rule categories configuration
const RULE_CATEGORIES = {
  behavioral: { label: 'Behavioral', color: '#2563EB', icon: '📊', description: 'Track website behavior and interactions' },
  engagement: { label: 'Engagement', color: '#16A34A', icon: '💬', description: 'Email opens, clicks, and content engagement' },
  firmographic: { label: 'Firmographic', color: '#F59E0B', icon: '🏢', description: 'Company and industry attributes' },
  fit: { label: 'Fit Score', color: '#9333EA', icon: '🎯', description: 'Ideal customer profile matching' },
};

// Event types for quick selection
const EVENT_TYPES = [
  { value: 'page_view', label: 'Page View' },
  { value: 'form_submit', label: 'Form Submission' },
  { value: 'button_click', label: 'Button Click' },
  { value: 'file_download', label: 'File Download' },
  { value: 'video_play', label: 'Video Play' },
  { value: 'email_open', label: 'Email Open' },
  { value: 'email_click', label: 'Email Click' },
  { value: 'demo_requested', label: 'Demo Requested' },
  { value: 'pricing_viewed', label: 'Pricing Viewed' },
  { value: 'signup_completed', label: 'Signup Completed' },
];

// Default rule template
const DEFAULT_RULE = {
  name: '',
  description: '',
  category: 'behavioral',
  event_type: '',
  conditions: {},
  points: 10,
  max_points: null,
  valid_within_days: 30,
  cooldown_hours: null,
  is_active: true,
  priority: 0,
};

// Mock rules for demo
const MOCK_RULES = [
  { id: 1, name: 'Pricing Page Visit', description: 'Visitor viewed pricing page', category: 'behavioral', event_type: 'pricing_viewed', points: 20, max_points: 40, valid_within_days: 30, is_active: true, conditions: { page_url: { contains: '/pricing' } } },
  { id: 2, name: 'Demo Request', description: 'Visitor requested a demo', category: 'engagement', event_type: 'demo_requested', points: 50, max_points: 50, valid_within_days: 60, is_active: true, conditions: {} },
  { id: 3, name: 'Email Click-through', description: 'Visitor clicked link in email', category: 'engagement', event_type: 'email_click', points: 15, max_points: 45, valid_within_days: 14, is_active: true, conditions: {} },
  { id: 4, name: 'Enterprise Company', description: 'Company has 500+ employees', category: 'firmographic', event_type: 'company_size', points: 25, max_points: 25, valid_within_days: null, is_active: true, conditions: { employee_count: { gte: 500 } } },
  { id: 5, name: 'Target Industry', description: 'Company in target industry', category: 'fit', event_type: 'industry_match', points: 30, max_points: 30, valid_within_days: null, is_active: true, conditions: { industry: { in: ['Technology', 'Finance', 'Healthcare'] } } },
  { id: 6, name: 'Multiple Page Views', description: 'Visited 5+ pages in a session', category: 'behavioral', event_type: 'page_view', points: 10, max_points: 30, valid_within_days: 7, is_active: false, conditions: { page_count: { gte: 5 } } },
];

function ScoringRules() {
  const navigate = useNavigate();
  
  // State
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [formData, setFormData] = useState(DEFAULT_RULE);
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [notification, setNotification] = useState(null);
  const [weights, setWeights] = useState({ behavioral: 35, engagement: 25, firmographic: 20, fit: 20 });
  const [weightsDialogOpen, setWeightsDialogOpen] = useState(false);

  // Fetch rules
  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const response = await salesRadarService.getScoringRules();
      setRules(response.data || []);
    } catch (err) {
      console.error(err);
      // Use mock data
      setRules(MOCK_RULES);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  // Filter rules
  const filteredRules = rules.filter(rule => {
    if (filterCategory !== 'all' && rule.category !== filterCategory) return false;
    if (searchQuery && !rule.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  // Group rules by category
  const groupedRules = filteredRules.reduce((acc, rule) => {
    const category = rule.category || 'behavioral';
    if (!acc[category]) acc[category] = [];
    acc[category].push(rule);
    return acc;
  }, {});

  // Handle dialog
  const handleOpenDialog = (rule = null) => {
    if (rule) {
      setEditingRule(rule);
      setFormData({ ...rule });
    } else {
      setEditingRule(null);
      setFormData({ ...DEFAULT_RULE });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingRule(null);
    setFormData(DEFAULT_RULE);
  };

  // Handle form changes
  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Save rule
  const handleSaveRule = async () => {
    try {
      if (editingRule) {
        await salesRadarService.updateScoringRule(editingRule.id, formData);
        setNotification({ type: 'success', message: 'Rule updated successfully' });
      } else {
        await salesRadarService.createScoringRule(formData);
        setNotification({ type: 'success', message: 'Rule created successfully' });
      }
      handleCloseDialog();
      fetchRules();
    } catch (err) {
      // Update local state for demo
      if (editingRule) {
        setRules(prev => prev.map(r => r.id === editingRule.id ? { ...formData, id: editingRule.id } : r));
      } else {
        setRules(prev => [...prev, { ...formData, id: Date.now() }]);
      }
      handleCloseDialog();
      setNotification({ type: 'success', message: editingRule ? 'Rule updated' : 'Rule created' });
    }
    setTimeout(() => setNotification(null), 3000);
  };

  // Delete rule
  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm('Are you sure you want to delete this rule?')) return;
    
    try {
      await salesRadarService.deleteScoringRule(ruleId);
    } catch (err) {
      // Update local state
    }
    setRules(prev => prev.filter(r => r.id !== ruleId));
    setNotification({ type: 'success', message: 'Rule deleted successfully' });
    setTimeout(() => setNotification(null), 3000);
  };

  // Toggle rule active status
  const handleToggleActive = async (rule) => {
    const updatedRule = { ...rule, is_active: !rule.is_active };
    setRules(prev => prev.map(r => r.id === rule.id ? updatedRule : r));
    setNotification({ type: 'success', message: `Rule ${rule.is_active ? 'deactivated' : 'activated'}` });
    setTimeout(() => setNotification(null), 3000);
  };

  // Duplicate rule
  const handleDuplicateRule = (rule) => {
    const newRule = { ...rule, name: `${rule.name} (Copy)`, id: undefined };
    setEditingRule(null);
    setFormData(newRule);
    setDialogOpen(true);
  };

  // Calculate total points per category
  const getCategoryStats = (category) => {
    const categoryRules = rules.filter(r => r.category === category);
    const activeRules = categoryRules.filter(r => r.is_active);
    const totalPoints = activeRules.reduce((sum, r) => sum + r.points, 0);
    return { total: categoryRules.length, active: activeRules.length, points: totalPoints };
  };

  return (
    <div>
      {/* Breadcrumb Navigation */}
      <nav style={{
        marginBottom: '1.5rem',
        display: 'flex',
        alignItems: 'center',
        color: 'var(--gray-600)',
        fontSize: '0.875rem'
      }}>
        <button onClick={() => navigate('/')} className="btn btn-link" style={{ padding: 0, color: 'var(--primary-600)' }}>
          Home
        </button>
        <span style={{ margin: '0 0.5rem', color: 'var(--gray-400)' }}>/</span>
        <button onClick={() => navigate('/sales-radar')} className="btn btn-link" style={{ padding: 0, color: 'var(--primary-600)' }}>
          Sales Radar
        </button>
        <span style={{ margin: '0 0.5rem', color: 'var(--gray-400)' }}>/</span>
        <span style={{ color: 'var(--gray-600)' }}>Scoring Rules</span>
      </nav>

      {/* Notification */}
      {notification && (
        <div style={{
          position: 'fixed',
          top: '1rem',
          right: '1rem',
          padding: '1rem 1.5rem',
          borderRadius: '8px',
          background: notification.type === 'success' ? '#DCFCE7' : '#FEE2E2',
          color: notification.type === 'success' ? '#16A34A' : '#DC2626',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          zIndex: 1000,
        }}>
          {notification.message}
        </div>
      )}

      {/* Header Card */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          paddingBottom: '1.5rem',
          borderBottom: '3px solid #F59E0B'
        }}>
          <div>
            <h2 style={{
              color: 'var(--gray-900)',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              margin: '0 0 0.25rem 0',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              🎯 Lead Scoring Rules
            </h2>
            <p style={{ color: 'var(--gray-600)', margin: 0, fontSize: '0.875rem' }}>
              Configure rules to automatically score leads based on their behavior and attributes
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button onClick={() => setWeightsDialogOpen(true)} className="btn btn-secondary">
              ⚖️ Category Weights
            </button>
            <button onClick={() => handleOpenDialog()} className="btn btn-primary">
              ➕ Add Rule
            </button>
          </div>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem' }}>
          <input
            type="text"
            placeholder="Search rules..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="form-input"
            style={{ width: '300px' }}
          />
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="form-select"
            style={{ width: '200px' }}
          >
            <option value="all">All Categories</option>
            {Object.entries(RULE_CATEGORIES).map(([key, cat]) => (
              <option key={key} value={key}>{cat.label}</option>
            ))}
          </select>
          <button onClick={fetchRules} className="btn btn-secondary">
            🔄 Refresh
          </button>
        </div>

        {/* Category Summary Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          marginBottom: '1.5rem'
        }}>
          {Object.entries(RULE_CATEGORIES).map(([key, cat]) => {
            const stats = getCategoryStats(key);
            return (
              <div
                key={key}
                onClick={() => setFilterCategory(filterCategory === key ? 'all' : key)}
                style={{
                  padding: '1rem',
                  borderRadius: '8px',
                  border: filterCategory === key ? `2px solid ${cat.color}` : '1px solid var(--gray-200)',
                  background: filterCategory === key ? `${cat.color}10` : 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '1.25rem' }}>{cat.icon}</span>
                  <span style={{ fontWeight: '600', color: cat.color }}>{cat.label}</span>
                </div>
                <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>
                  {stats.active} active / {stats.total} rules
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: '0.25rem' }}>
                  Up to {stats.points} points • {weights[key]}% weight
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Rules List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
          <div style={{ color: 'var(--gray-500)' }}>Loading rules...</div>
        </div>
      ) : (
        Object.entries(RULE_CATEGORIES).map(([categoryKey, category]) => {
          const categoryRules = groupedRules[categoryKey] || [];
          if (filterCategory !== 'all' && filterCategory !== categoryKey) return null;
          if (categoryRules.length === 0) return null;

          return (
            <div key={categoryKey} className="card" style={{ marginBottom: '1rem' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '1rem',
                paddingBottom: '0.75rem',
                borderBottom: `2px solid ${category.color}`,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontSize: '1.25rem' }}>{category.icon}</span>
                  <h3 style={{ margin: 0, color: 'var(--gray-900)' }}>{category.label}</h3>
                  <span style={{
                    padding: '0.125rem 0.5rem',
                    borderRadius: '9999px',
                    fontSize: '0.75rem',
                    background: 'var(--gray-100)',
                    color: 'var(--gray-600)',
                  }}>
                    {categoryRules.length} rules
                  </span>
                </div>
                <span style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>
                  {category.description}
                </span>
              </div>

              {categoryRules.map(rule => (
                <div
                  key={rule.id}
                  style={{
                    padding: '1rem',
                    marginBottom: '0.75rem',
                    borderRadius: '8px',
                    border: '1px solid var(--gray-200)',
                    borderLeft: `4px solid ${category.color}`,
                    background: rule.is_active ? 'white' : 'var(--gray-50)',
                    opacity: rule.is_active ? 1 : 0.7,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                        <span style={{ fontWeight: '600', color: 'var(--gray-900)' }}>{rule.name}</span>
                        {!rule.is_active && (
                          <span style={{
                            padding: '0.125rem 0.5rem',
                            borderRadius: '9999px',
                            fontSize: '0.625rem',
                            background: 'var(--gray-200)',
                            color: 'var(--gray-600)',
                          }}>
                            Inactive
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--gray-500)', marginBottom: '0.5rem' }}>
                        {rule.description || 'No description'}
                      </div>
                      
                      {/* Rule details */}
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <span style={{
                          padding: '0.25rem 0.5rem',
                          borderRadius: '6px',
                          fontSize: '0.75rem',
                          background: '#DBEAFE',
                          color: '#2563EB',
                          fontWeight: '500',
                        }}>
                          +{rule.points} pts
                        </span>
                        {rule.max_points && (
                          <span style={{
                            padding: '0.25rem 0.5rem',
                            borderRadius: '6px',
                            fontSize: '0.75rem',
                            border: '1px solid var(--gray-300)',
                          }}>
                            Max: {rule.max_points}
                          </span>
                        )}
                        {rule.valid_within_days && (
                          <span style={{
                            padding: '0.25rem 0.5rem',
                            borderRadius: '6px',
                            fontSize: '0.75rem',
                            border: '1px solid var(--gray-300)',
                          }}>
                            ⏱️ {rule.valid_within_days}d window
                          </span>
                        )}
                        {rule.event_type && (
                          <span style={{
                            padding: '0.25rem 0.5rem',
                            borderRadius: '6px',
                            fontSize: '0.75rem',
                            background: 'var(--gray-100)',
                          }}>
                            {EVENT_TYPES.find(e => e.value === rule.event_type)?.label || rule.event_type}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={rule.is_active}
                          onChange={() => handleToggleActive(rule)}
                          style={{ marginRight: '0.25rem' }}
                        />
                        <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>Active</span>
                      </label>
                      <button
                        onClick={() => handleDuplicateRule(rule)}
                        title="Duplicate"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem' }}
                      >
                        📋
                      </button>
                      <button
                        onClick={() => handleOpenDialog(rule)}
                        title="Edit"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem' }}
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDeleteRule(rule.id)}
                        title="Delete"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem' }}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          );
        })
      )}

      {/* Add/Edit Rule Dialog */}
      {dialogOpen && (
        <>
          <div
            onClick={handleCloseDialog}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              zIndex: 999,
            }}
          />
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'white',
            borderRadius: '12px',
            padding: '1.5rem',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
            zIndex: 1000,
            width: '500px',
            maxHeight: '80vh',
            overflowY: 'auto',
          }}>
            <h3 style={{ margin: '0 0 1.5rem 0', color: 'var(--gray-900)' }}>
              {editingRule ? 'Edit Rule' : 'Create New Rule'}
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--gray-700)' }}>
                  Rule Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  className="form-input"
                  style={{ width: '100%' }}
                  placeholder="e.g., Pricing Page Visit"
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--gray-700)' }}>
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleFormChange('description', e.target.value)}
                  className="form-textarea"
                  style={{ width: '100%', minHeight: '80px' }}
                  placeholder="Describe what this rule measures"
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--gray-700)' }}>
                    Category
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => handleFormChange('category', e.target.value)}
                    className="form-select"
                    style={{ width: '100%' }}
                  >
                    {Object.entries(RULE_CATEGORIES).map(([key, cat]) => (
                      <option key={key} value={key}>{cat.icon} {cat.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--gray-700)' }}>
                    Event Type
                  </label>
                  <select
                    value={formData.event_type}
                    onChange={(e) => handleFormChange('event_type', e.target.value)}
                    className="form-select"
                    style={{ width: '100%' }}
                  >
                    <option value="">Select event type</option>
                    {EVENT_TYPES.map(event => (
                      <option key={event.value} value={event.value}>{event.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--gray-700)' }}>
                    Points *
                  </label>
                  <input
                    type="number"
                    value={formData.points}
                    onChange={(e) => handleFormChange('points', parseInt(e.target.value) || 0)}
                    className="form-input"
                    style={{ width: '100%' }}
                    min="0"
                    max="100"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--gray-700)' }}>
                    Max Points
                  </label>
                  <input
                    type="number"
                    value={formData.max_points || ''}
                    onChange={(e) => handleFormChange('max_points', e.target.value ? parseInt(e.target.value) : null)}
                    className="form-input"
                    style={{ width: '100%' }}
                    min="0"
                    placeholder="No limit"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--gray-700)' }}>
                    Valid Days
                  </label>
                  <input
                    type="number"
                    value={formData.valid_within_days || ''}
                    onChange={(e) => handleFormChange('valid_within_days', e.target.value ? parseInt(e.target.value) : null)}
                    className="form-input"
                    style={{ width: '100%' }}
                    min="1"
                    placeholder="Forever"
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => handleFormChange('is_active', e.target.checked)}
                  />
                  <span style={{ fontWeight: '500', color: 'var(--gray-700)' }}>Rule is active</span>
                </label>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem', justifyContent: 'flex-end' }}>
              <button onClick={handleCloseDialog} className="btn btn-secondary">
                Cancel
              </button>
              <button
                onClick={handleSaveRule}
                className="btn btn-primary"
                disabled={!formData.name}
              >
                {editingRule ? 'Update Rule' : 'Create Rule'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Category Weights Dialog */}
      {weightsDialogOpen && (
        <>
          <div
            onClick={() => setWeightsDialogOpen(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              zIndex: 999,
            }}
          />
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'white',
            borderRadius: '12px',
            padding: '1.5rem',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
            zIndex: 1000,
            width: '400px',
          }}>
            <h3 style={{ margin: '0 0 1rem 0', color: 'var(--gray-900)' }}>
              ⚖️ Category Weights
            </h3>
            <p style={{ color: 'var(--gray-600)', marginBottom: '1.5rem', fontSize: '0.875rem' }}>
              Adjust how much each category contributes to the overall lead score. Total must equal 100%.
            </p>

            {Object.entries(RULE_CATEGORIES).map(([key, cat]) => (
              <div key={key} style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span>{cat.icon}</span>
                    <span style={{ fontWeight: '500' }}>{cat.label}</span>
                  </span>
                  <span style={{ fontWeight: '600', color: cat.color }}>{weights[key]}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={weights[key]}
                  onChange={(e) => setWeights(prev => ({ ...prev, [key]: parseInt(e.target.value) }))}
                  style={{ width: '100%' }}
                />
              </div>
            ))}

            <div style={{
              padding: '0.75rem',
              borderRadius: '8px',
              background: Object.values(weights).reduce((a, b) => a + b, 0) === 100 ? '#DCFCE7' : '#FEE2E2',
              color: Object.values(weights).reduce((a, b) => a + b, 0) === 100 ? '#16A34A' : '#DC2626',
              marginBottom: '1rem',
              textAlign: 'center',
              fontWeight: '500',
            }}>
              Total: {Object.values(weights).reduce((a, b) => a + b, 0)}%
              {Object.values(weights).reduce((a, b) => a + b, 0) !== 100 && ' (must equal 100%)'}
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setWeightsDialogOpen(false)} className="btn btn-secondary">
                Cancel
              </button>
              <button
                onClick={() => {
                  setWeightsDialogOpen(false);
                  setNotification({ type: 'success', message: 'Weights updated successfully' });
                  setTimeout(() => setNotification(null), 3000);
                }}
                className="btn btn-primary"
                disabled={Object.values(weights).reduce((a, b) => a + b, 0) !== 100}
              >
                Save Weights
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default ScoringRules;
